import React from "react";
import {Button, Col, Form, Input, InputNumber, Modal, Row} from "antd";

import {messageType, showMessages} from "../../helpers/message";
import {GSTRegistrationNumberText, nothingToUpdate, TaxRegistrationNumberText} from "../../helpers/text";
import {formItemFullWidth} from "../../assets/styles"
import {useUpdateCompanyMutation} from "../../store/api/companyApi";
import {useGetCompanyInfoQuery} from "../../store/api/settingsApi";
import {Company} from "../../types/companyTypes";
import {BaseError} from "../../types/apiTypes";
import {AccountBusinessInfoEditParam} from "../../types/accountTypes";

const AccountBusinessInfoEdit = ({companyDataProp, isDrawerVisible, setDrawerVisible}: AccountBusinessInfoEditParam) => {
    const [companyData, setCompanyData] = React.useState<Company>(companyDataProp);
    const [updateCompany, {isSuccess, isError, error, isLoading, reset}] = useUpdateCompanyMutation();
    const {data: companyInfo} = useGetCompanyInfoQuery();
    const clientCountry = companyInfo?.country;
    const taxNumberText = clientCountry === 'CA' ? GSTRegistrationNumberText : TaxRegistrationNumberText;

    React.useEffect(() => {
        if (isSuccess) {
            showMessages('Your changes have been successfully saved!', messageType.success);
            setDrawerVisible(false);
            reset();
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
            reset();
        }
    }, [error, isError, isSuccess, reset, setDrawerVisible]);

    const handleFinish = async () => {
        if (!sanityCheck(companyData)) return;
        updateCompany(companyData);
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = e.target;

        setCompanyData((prevState) => ({
            ...(prevState as Company),
            [name]: value,
        }));
    };

    const handleNumberChange = (value: number | null) => {
           setCompanyData(prevState => ({
            ...prevState,
            'company_size' : value
        }));
    };

    const sanityCheck = (state: Company): boolean => {
        const {
            business_name,
            business_registration_number,
            company_size,
            dba_name,
            gst_hst_number,
            industry_code,
            name_on_contract,
            website_address,
        } = state;

        if (
            business_name !== companyDataProp?.business_name ||
            business_registration_number !== companyDataProp?.business_registration_number ||
            company_size !== companyDataProp?.company_size ||
            dba_name !== companyDataProp?.dba_name ||
            gst_hst_number !== companyDataProp?.gst_hst_number ||
            industry_code !== companyDataProp?.industry_code ||
            name_on_contract !== companyDataProp?.name_on_contract ||
            website_address !== companyDataProp?.website_address
        ) {
            return true;
        }

        showMessages(nothingToUpdate, messageType.error);
        return false;
    };

    return (
        <Modal
            title="Business Details"
            open={isDrawerVisible}
            onCancel={() => setDrawerVisible(false)}
            footer={[
                <Button key="cancel" onClick={() => setDrawerVisible(false)}>
                    Cancel
                </Button>,
                <Button
                    form="company-data"
                    key="submit"
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                >
                    Save
                </Button>,
               ]}
        >
            <Form id="company-data"
                  layout="vertical"
                  onFinish={handleFinish}
                  initialValues={{
                      'Business Name': companyData?.business_name,
                      'DBA Name': companyData?.dba_name,
                      'Name On Contract': companyData?.name_on_contract,
                      'GST/HST Number': companyData?.gst_hst_number,
                      'Business Registration Number': companyData?.business_registration_number,
                      'Website Address': companyData?.website_address,
                      'Industry Code': companyData?.industry_code,
                      'Company Size': companyData?.company_size,
                  }}>
                <Row gutter={16}>
                    <Col xs={24}>
                        <Form.Item label="Business Name"
                                   name="Business Name"
                                   rules={[{required: true}]}>
                            <Input
                                name='business_name'
                                style={{...formItemFullWidth}}
                                placeholder="Enter business name"
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="DBA Name"
                                   name="DBA Name">
                            <Input
                                name="dba_name"
                                style={{...formItemFullWidth}}
                                placeholder="Enter DBA name"
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Name On Contract"
                                   name="Name On Contract"
                                   rules={[{required: true}]}>
                            <Input
                                name="name_on_contract"
                                style={{...formItemFullWidth}}
                                placeholder="Enter name on contract"
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="GST/HST Number"
                                   rules={[{
                                   required: (!companyData?.gst_hst_number && !companyData?.business_registration_number),
                                   message: `${taxNumberText} or Business Registration Number is required`
                                   }]}
                                   label={taxNumberText}>
                            <Input
                                name="gst_hst_number"
                                style={{...formItemFullWidth}}
                                placeholder={`Enter ${taxNumberText}`}
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item name="Business Registration Number"
                                   rules={[{
                                   required: (!companyData?.gst_hst_number && !companyData?.business_registration_number),
                                   message: `${taxNumberText} or Business Registration Number is required`
                                   }]}
                                   label="Business Registration Number"
                                   >
                            <Input
                                name="business_registration_number"
                                style={{...formItemFullWidth}}
                                placeholder="Enter business registration number"
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Website Address"
                                   name="Website Address">
                            <Input
                                name="website_address"
                                style={{...formItemFullWidth}}
                                placeholder="Enter website address"
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Industry Code"
                                   name="Industry Code">
                            <Input
                                name="industry_code"
                                style={{...formItemFullWidth}}
                                placeholder="Enter industry code"
                                onChange={handleChange}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Company Size"
                                   name="Company Size"
                                   rules={[{type: 'number'}]}>
                            <InputNumber
                                name="company_size"
                                style={{...formItemFullWidth}}
                                min={0}
                                placeholder="Enter company size"
                                onChange={handleNumberChange}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
}

export default AccountBusinessInfoEdit;
